import { inReach } from 'layout/NewDesignLayout/Sidebar/usePinDrag';
import { animate } from 'motion/mini';
import type { AnimationOptions, AnimationPlaybackControls } from 'motion/react';

type Box = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'>;

/**
 * The slot the magnet pulls a widget held at `x`, `y` into, none when the pointer is out of the pins' reach of every
 * slot. The slot the pointer is inside wins; outside them all the one the widget is `held` in keeps it, so the gap
 * between two slots does not flicker; and with neither, the nearest does.
 */
export function slotInReach<Id>(x: number, y: number, slots: Iterable<[Id, Box]>, held?: Id): Id | undefined {
    let slot: Id | undefined;
    let least = Infinity;
    for (const [id, box] of slots) {
        if (!inReach(x, y, box)) continue;
        const gap = Math.hypot(Math.max(box.left - x, 0, x - box.right), Math.max(box.top - y, 0, y - box.bottom));
        const pull = gap === 0 ? -1 : id === held ? 0 : gap;
        if (pull < least) {
            slot = id;
            least = pull;
        }
    }
    return slot;
}

/** A cell's box by its corner, in the grid's own coordinates. */
type Place = { left: number; top: number; width: number; height: number };

/** Where a cell rests: its layout box, which no glide in progress changes. */
export const restOf = (cell: HTMLElement): Place => ({
    left: cell.offsetLeft,
    top: cell.offsetTop,
    width: cell.offsetWidth,
    height: cell.offsetHeight,
});

const cellsOf = (grid: HTMLElement) => [...grid.children] as HTMLElement[];

/** Each cell on its way to where a change left it: what it gets there with. */
const journeys = new WeakMap<Element, { arrived: Promise<void>; arrive: () => void }>();

/**
 * Marks a cell as on its way, until `arrive` says it is there or for `ms` at most: Motion says nothing of a glide
 * that another one cuts short.
 */
export function setOff(cell: Element, ms: number) {
    let done = () => {};
    const arrived = new Promise<void>((resolve) => {
        done = resolve;
        setTimeout(resolve, ms);
    });
    journeys.set(cell, { arrived, arrive: done });
}

export const arrive = (cell: Element) => journeys.get(cell)?.arrive();

/**
 * Settles once every cell of `grid` is at rest. Motion sets a change's glides off in a microtask it queues as the
 * change is drawn: the cells are looked at after it.
 */
export const atRest = (grid: HTMLElement) =>
    Promise.resolve().then(() => Promise.all(cellsOf(grid).map((cell) => journeys.get(cell)?.arrived)));

/** The fade each cell that turns up somewhere is on, or was on last. */
const fades = new WeakMap<Element, AnimationPlaybackControls>();

/** Lets a cell that does not travel to where a change leaves it show up there, fading in. */
export function turnUp(cell: Element, timing: AnimationOptions) {
    fades.set(cell, animate(cell, { opacity: [0, 1] }, timing));
}

/** Cuts short the fade a cell is on, which then shows at once. */
export const settle = (cell: Element) => fades.get(cell)?.cancel();

/**
 * What a cell showed ahead of a change of its size: its box, a still copy of its content, and where its other parts
 * stood.
 */
const held = new WeakMap<Element, { was: Place; content: HTMLElement; parts: [HTMLElement, number, number][] }>();

/**
 * Keeps what a cell shows, to be called ahead of a change of its size. `reveal` then lets the old content go as the
 * new one comes, each at its own size, instead of stretching the one into the box of the other.
 */
export function hold(cell: HTMLElement) {
    const [content, ...parts] = [...cell.children] as HTMLElement[];
    if (!content) return;
    held.set(cell, {
        was: restOf(cell),
        // ponytail: a still copy, as the lifted widget's is: a list in it starts from the top again.
        content: content.cloneNode(true) as HTMLElement,
        parts: parts.map((part) => [part, part.offsetLeft, part.offsetTop]),
    });
}

/** A layer of the size `own`, cut to the box `to` where that one is the smaller. */
const cut = (own: Place, to: Place) =>
    `inset(0 ${Math.max(own.width - to.width, 0)}px ${Math.max(own.height - to.height, 0)}px 0)`;

/**
 * Shows each cell of `grid` that `hold` kept at its new size, once it is drawn at it, with nothing drawn stretched:
 * what it shows changes in a box that goes from the old size to the new one, the kept content fading out as the new
 * one fades in, and its other parts slide from where they stood. Motion takes the cell itself to its new corner.
 */
export function reveal(grid: HTMLElement, timing: AnimationOptions) {
    for (const cell of cellsOf(grid)) {
        const kept = held.get(cell);
        const content = cell.firstElementChild as HTMLElement | null;
        held.delete(cell);
        if (!kept || !content) continue;
        const { was, content: old } = kept;
        const rest = restOf(cell);
        // Not drawn, or the same size after all.
        if (!rest.width || !rest.height || (rest.width === was.width && rest.height === was.height)) continue;
        // How far from where it stands now each of the cell's other parts stood.
        const shifts = kept.parts.map(
            ([part, left, top]) => [part, left - part.offsetLeft, top - part.offsetTop] as const
        );

        // Motion leaves the last frame inline, and a cut, even of nothing, would clip the widget's shadow.
        const uncut = () => content.style.removeProperty('clip-path');
        void animate(content, { opacity: [0, 1], clipPath: [cut(rest, was), 'inset(0)'] }, timing).finished.then(uncut);
        // Over the new content, at the corner they share and at the size it had.
        old.classList.add('absolute', 'top-0', 'left-0');
        old.style.width = `${was.width}px`;
        old.style.height = `${was.height}px`;
        cell.append(old);
        void animate(old, { opacity: [1, 0], clipPath: ['inset(0)', cut(was, rest)] }, timing).finished.then(() =>
            old.remove()
        );
        for (const [part, x, y] of shifts) {
            if (x || y) animate(part, { transform: [`translate(${x}px, ${y}px)`, 'none'] }, timing);
        }
    }
}
